import * as React from "react";
import {
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

function fmtFecha(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString("es-CO");
}

const estadoChip = (estado: string) => {
  const e = String(estado || "").trim();
  if (e === "aprobado") return <Chip size="small" label="Aprobado" color="success" />;
  if (e === "rechazado") return <Chip size="small" label="Rechazado" color="error" />;
  if (e === "pendiente_jefe") return <Chip size="small" label="Pendiente jefe" color="warning" />;
  if (e === "pendiente_carga_solicitante") return <Chip size="small" label="Pendiente carga solicitante" color="info" />;
  if (e) return <Chip size="small" label={e} variant="outlined" />;
  return <Chip size="small" label="Sin estado" variant="outlined" />;
};

export type CartaRemisoriaTrazabilidadVistaProps = {
  /** Objeto `carta_remisoria_jefe` del checklist */
  data: Record<string, any>;
  /** Título opcional (p. ej. código de proyecto en vista global) */
  tituloExtra?: string;
};

/**
 * Vista detallada de trazabilidad de carta remisoria (historial completo + fechas clave).
 */
export default function CartaRemisoriaTrazabilidadVista({ data, tituloExtra }: CartaRemisoriaTrazabilidadVistaProps) {
  const historial = React.useMemo(() => {
    const raw = Array.isArray(data?.historial) ? data.historial : [];
    return [...raw].sort((a: any, b: any) => {
      const ta = new Date(String(a?.fecha || 0)).getTime();
      const tb = new Date(String(b?.fecha || 0)).getTime();
      return tb - ta;
    });
  }, [data?.historial]);

  const hayFlujo = Boolean(
    String(data?.estado || "").trim() ||
      historial.length ||
      data?.habilitado_diseno_at ||
      data?.carta_cargada_at ||
      data?.comprobante_pago_cargado_at ||
      data?.documento_cargado_at ||
      data?.resuelto_jefe_at,
  );

  if (!hayFlujo) {
    return (
      <Typography variant="body2" color="text.secondary">
        Aún no hay trazabilidad de carta remisoria para este proyecto (el flujo se habilita al aprobar el diseño).
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {tituloExtra && (
        <Typography variant="subtitle2" fontWeight={700}>
          {tituloExtra}
        </Typography>
      )}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          Estado actual:
        </Typography>
        {estadoChip(String(data?.estado || ""))}
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 1 }}>
          Fechas y responsables
        </Typography>
        <Grid container spacing={1}>
          {[
            ["Habilitado tras diseño", data?.habilitado_diseno_at],
            ["Carta remisoria cargada", data?.carta_cargada_at],
            ["Comprobante de pago cargado", data?.comprobante_pago_cargado_at],
            ["Documentos completos (solicitante)", data?.documento_cargado_at],
            ["Resolución jefe", data?.resuelto_jefe_at],
            ["Resuelto por", data?.resuelto_jefe_por],
            ["Comentario jefe", data?.comentario_jefe],
          ].map(([label, val]) => {
            const str = val != null && String(val).trim() !== "" ? String(val) : "";
            let mostrar = "—";
            if (str) {
              const parsed = Date.parse(str);
              const pareceFecha =
                label !== "Resuelto por" &&
                label !== "Comentario jefe" &&
                !Number.isNaN(parsed) &&
                str.length >= 8;
              mostrar = pareceFecha ? fmtFecha(str) : str;
            }
            return (
              <Grid item xs={12} sm={6} key={String(label)}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {label}
                </Typography>
                <Typography variant="body2">{mostrar}</Typography>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      <Box>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Historial completo ({historial.length} evento{historial.length === 1 ? "" : "s"})
        </Typography>
        {historial.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay eventos en el historial. Si ya hubo acciones, revise que no se haya reemplazado el checklist sin conservar `historial`.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={180}>Fecha</TableCell>
                <TableCell>Acción</TableCell>
                <TableCell>Detalle</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Decisión</TableCell>
                <TableCell>Comentario</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historial.map((h: any, idx: number) => (
                <TableRow key={`${h?.fecha || idx}-${idx}`}>
                  <TableCell>{fmtFecha(h?.fecha)}</TableCell>
                  <TableCell>{String(h?.accion || "—")}</TableCell>
                  <TableCell>{String(h?.detalle || "—")}</TableCell>
                  <TableCell>{String(h?.usuario || h?.usuario_sesion || "—")}</TableCell>
                  <TableCell>{h?.decision != null ? String(h.decision) : "—"}</TableCell>
                  <TableCell>{String(h?.comentario || "—")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Stack>
  );
}
